import React, { useState, useEffect, useMemo } from 'react';
import {
  Container, Typography, Box, CircularProgress, Alert, AlertTitle, Accordion,
  AccordionSummary, AccordionDetails, Grid, Card, CardContent, Button, Link as MuiLink, Paper
} from '@mui/material';
// Updated Icon Imports for MUI v5
import SchoolIcon from '@mui/icons-material/School'; 
import StarIcon from '@mui/icons-material/Star'; 
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium'; 
import ArticleIcon from '@mui/icons-material/Article'; 
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo'; 
import DownloadIcon from '@mui/icons-material/Download'; 
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';

import { useAuth } from '../contexts/AuthContext';
import { Tier } from '../contexts/AuthContext'; // Import Tier from AuthContext
import { useLanguage } from '../contexts/LanguageContext'; // Import language context
import { translations } from '../translations'; // Import translations object
import PageHeader from '../components/PageHeader'; // Import PageHeader
import ProtectedContent from '../components/ProtectedContent';
import { SUBSCRIPTION_TIERS } from '../components/UserSubscriptionManager';

// Define Module Item Structure (e.g., a PDF)
interface ModuleItem {
  id: string;
  title: { [key: string]: string }; // For translation
  description: { [key: string]: string }; // For translation
  pdfUrl: string;
  fileName: string;
  order: number; // To determine sequence within the module
}

// Define Module Structure
interface Module {
  id: string;
  title: { [key: string]: string }; // For translation
  description: { [key: string]: string }; // For translation
  introVideoUrl: string; // URL for module intro video
  accessTier: Exclude<Tier, 'None'>; // Tier required for this module
  items: ModuleItem[]; // Array of PDFs or other content items
}

// --- Mock Data with Translation Structure ---
const mockModules: Module[] = [
  {
    id: 'self-module-1',
    title: { nl: 'Module 1: Zelfbewustzijn & Reflectie', en: 'Module 1: Self-awareness & Reflection' },
    description: { nl: 'Ontdek je kernwaarden en begin met zelfreflectie. Dit is het startpunt van je reis.', en: 'Discover your core values and start self-reflection. This is the starting point of your journey.' },
    introVideoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ', // General intro moved here
    accessTier: 'Self',
    items: [
      { id: 's1-item1', title: { nl: 'Introductie Zelfbewustzijn', en: 'Introduction to Self-awareness' }, description: { nl: 'Wat is zelfbewustzijn en waarom is het belangrijk?', en: 'What is self-awareness and why is it important?' }, pdfUrl: '/content/Self_Module1_Intro.pdf', fileName: 'Self_Module1_Intro.pdf', order: 1 },
      { id: 's1-item2', title: { nl: 'Kernwaarden Oefening', en: 'Core Values Exercise' }, description: { nl: 'Identificeer je persoonlijke kernwaarden.', en: 'Identify your personal core values.' }, pdfUrl: '/content/Self_Module1_Kernwaarden.pdf', fileName: 'Self_Module1_Kernwaarden.pdf', order: 2 },
      { id: 's1-item3', title: { nl: 'Reflectie Technieken', en: 'Reflection Techniques' }, description: { nl: 'Leer effectieve manieren om te reflecteren.', en: 'Learn effective ways to reflect.' }, pdfUrl: '/content/Self_Module1_Reflectie.pdf', fileName: 'Self_Module1_Reflectie.pdf', order: 3 },
    ]
  },
  {
    id: 'self-module-2',
    title: { nl: 'Module 2: Basis Stressmanagement', en: 'Module 2: Basic Stress Management' },
    description: { nl: 'Leer de basis van stress herkennen en beheren.', en: 'Learn the basics of recognizing and managing stress.' },
    introVideoUrl: 'https://www.youtube.com/embed/VIDEO_ID_SELF_2', // Placeholder
    accessTier: 'Self',
    items: [
      { id: 's2-item1', title: { nl: 'Stress Signalen Herkennen', en: 'Recognizing Stress Signals' }, description: { nl: 'Hoe herken je stress in je lichaam en geest?', en: 'How do you recognize stress in your body and mind?' }, pdfUrl: '/content/Self_Module2_Signalen.pdf', fileName: 'Self_Module2_Signalen.pdf', order: 1 },
      { id: 's2-item2', title: { nl: 'Ademhalingsoefeningen', en: 'Breathing Exercises' }, description: { nl: 'Eenvoudige oefeningen voor directe ontspanning.', en: 'Simple exercises for immediate relaxation.' }, pdfUrl: '/content/Self_Module2_Ademhaling.pdf', fileName: 'Self_Module2_Ademhaling.pdf', order: 2 },
    ]
  },
  {
    id: 'premium-module-1',
    title: { nl: 'Module 3: Effectieve Communicatie', en: 'Module 3: Effective Communication' },
    description: { nl: 'Verbeter je communicatie in werk en privé.', en: 'Improve your communication at work and in private life.' },
    introVideoUrl: 'https://www.youtube.com/embed/VIDEO_ID_PREMIUM_1', // Placeholder
    accessTier: 'Premium',
    items: [
      { id: 'p1-item1', title: { nl: 'Actief Luisteren', en: 'Active Listening' }, description: { nl: 'De kunst van echt horen wat gezegd wordt.', en: 'The art of truly hearing what is said.' }, pdfUrl: '/content/Premium_Module3_Luisteren.pdf', fileName: 'Premium_Module3_Luisteren.pdf', order: 1 },
      { id: 'p1-item2', title: { nl: 'Feedback Geven & Ontvangen', en: 'Giving & Receiving Feedback' }, description: { nl: 'Constructieve feedback als groeimiddel.', en: 'Constructive feedback as a growth tool.' }, pdfUrl: '/content/Premium_Module3_Feedback.pdf', fileName: 'Premium_Module3_Feedback.pdf', order: 2 },
      { id: 'p1-item3', title: { nl: 'Non-verbale Communicatie', en: 'Non-verbal Communication' }, description: { nl: 'Lichaamstaal begrijpen en inzetten.', en: 'Understanding and using body language.' }, pdfUrl: '/content/Premium_Module3_NonVerbaal.pdf', fileName: 'Premium_Module3_NonVerbaal.pdf', order: 3 },
    ]
  },
  {
    id: 'complete-module-1',
    title: { nl: 'Module 4: Loopbaan Strategie & Visie', en: 'Module 4: Career Strategy & Vision' },
    description: { nl: 'Ontwikkel een lange termijn visie voor je carrière.', en: 'Develop a long-term vision for your career.' },
    introVideoUrl: 'https://www.youtube.com/embed/VIDEO_ID_COMPLETE_1', // Placeholder
    accessTier: 'Complete',
    items: [
      { id: 'c1-item1', title: { nl: 'Carrièreplanning Model', en: 'Career Planning Model' }, description: { nl: 'Een bewezen model voor loopbaanplanning.', en: 'A proven model for career planning.' }, pdfUrl: '/content/Complete_Module4_Model.pdf', fileName: 'Complete_Module4_Model.pdf', order: 1 },
      { id: 'c1-item2', title: { nl: 'Netwerken voor Groei', en: 'Networking for Growth' }, description: { nl: 'Strategisch netwerken voor kansen.', en: 'Strategic networking for opportunities.' }, pdfUrl: '/content/Complete_Module4_Netwerken.pdf', fileName: 'Complete_Module4_Netwerken.pdf', order: 2 },
    ]
  },
];
// --- End Mock Data ---

// Helper function to sort modules (simple sort by ID for now)
const sortModules = (a: Module, b: Module) => {
  const tierOrder: Record<Exclude<Tier, 'None'>, number> = { Self: 1, Premium: 2, Complete: 3 };
  if (tierOrder[a.accessTier] !== tierOrder[b.accessTier]) {
      return tierOrder[a.accessTier] - tierOrder[b.accessTier];
  }
  return a.id.localeCompare(b.id);
};

const MyContent: React.FC = () => {
  const { currentUser, loading: authLoading } = useAuth();
  const { language } = useLanguage(); // Use language hook
  const [userTier, setUserTier] = useState<Tier>('None');
  // --- Restore State Variables --- 
  const [allModules, setAllModules] = useState<Module[]>([]); 
  const [loadingContent, setLoadingContent] = useState<boolean>(true); // <<< RESTORED
  const [expandedModule, setExpandedModule] = useState<string | false>(false); 
  // --- End Restore State ---

  // --- Restore useEffect Hooks ---
  useEffect(() => {
    // Simulate fetching module data 
    const fetchModules = async () => {
      setLoadingContent(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setAllModules(mockModules);
      setLoadingContent(false);
    };
    fetchModules();
  }, []);

  useEffect(() => {
    // Set user tier
    if (!authLoading) {
      const tierFromAuth = currentUser?.subscription?.tier as Tier | undefined;
      setUserTier(tierFromAuth || 'None');
    }
  }, [currentUser, authLoading]);
  // --- End Restore useEffect Hooks ---

  // --- Restore groupedModules Calculation --- 
  const groupedModules = useMemo(() => {
    if (userTier === 'None') return {};
    const accessibleModules = allModules.filter(module => {
      switch (userTier) {
        case 'Complete': return true;
        case 'Premium': return module.accessTier === 'Self' || module.accessTier === 'Premium';
        case 'Self': return module.accessTier === 'Self';
        default: return false;
      }
    });
    accessibleModules.sort(sortModules);
    const groups: { [key in Exclude<Tier, 'None'>]?: Module[] } = {};
    accessibleModules.forEach(module => {
      const tier = module.accessTier;
      if (!groups[tier]) { groups[tier] = []; }
      groups[tier]?.push(module);
    });
    return groups;
  }, [userTier, allModules]);
  // --- End Restore groupedModules ---

  // --- Restore useEffect for Initial Expansion ---
   useEffect(() => {
    if (!loadingContent && Object.keys(groupedModules).length > 0) {
      const firstTierKey = Object.keys(groupedModules).sort((a, b) => {
         const tierOrder: Record<Exclude<Tier, 'None'>, number> = { Self: 1, Premium: 2, Complete: 3 };
         return tierOrder[a as Exclude<Tier, 'None'>] - tierOrder[b as Exclude<Tier, 'None'>];
      })[0] as Exclude<Tier, 'None'> | undefined;
      const firstModule = firstTierKey ? groupedModules[firstTierKey]?.[0] : undefined;
      if (firstModule) { setExpandedModule(firstModule.id); }
    }
   }, [loadingContent, groupedModules]);
  // --- End Restore useEffect for Initial Expansion ---

   // --- Restore Accordion Handler --- 
   const handleAccordionChange = (moduleId: string) => 
      (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedModule(isExpanded ? moduleId : false);
   };
   // --- End Restore Accordion Handler ---

  const isLoading = authLoading || loadingContent; // Now loadingContent is defined

  const getTierDetails = (tier: Exclude<Tier, 'None'>) => {
    // Use translation keys for labels
    const key = tier.toLowerCase() as keyof typeof translations['nl']['tierLabels'];
    switch (tier) {
      case 'Self': return { icon: <SchoolIcon sx={{ mr: 1 }}/>, label: translations[language].tierLabels[key], color: 'info' as const };
      case 'Premium': return { icon: <StarIcon sx={{ mr: 1 }} />, label: translations[language].tierLabels[key], color: 'warning' as const };
      case 'Complete': return { icon: <WorkspacePremiumIcon sx={{ mr: 1 }}/>, label: translations[language].tierLabels[key], color: 'success' as const };
    }
  };

  return (
    <>
      {/* Add PageHeader */}
      <PageHeader
        title={translations[language].myContent.pageTitle}
      />
      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>{translations[language].myContent.loading}...</Typography> {/* Translate */}
          </Box>
        ) : userTier === 'None' ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            <AlertTitle>{translations[language].myContent.noAccessTitle}</AlertTitle> {/* Translate */}
            {translations[language].myContent.noAccessMessage}{/* Translate */} <MuiLink href="/premium">{translations[language].myContent.premiumOptionsLink}</MuiLink>.
          </Alert>
        ) : (
          <Box>
              <Box> 
                {Object.keys(groupedModules).length === 0 ? (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        <AlertTitle>{translations[language].myContent.noModulesTitle}</AlertTitle> {/* Translate */}
                        {translations[language].myContent.noModulesMessage.replace('{userTier}', userTier)} {/* Translate */}
                    </Alert>
                ) : ( 
                   (['Self', 'Premium', 'Complete'] as Exclude<Tier, 'None'>[]).map(tierKey => {
                    const modulesInTier = groupedModules[tierKey];
                    if (!modulesInTier || modulesInTier.length === 0) return null;

                    const tierDetails = getTierDetails(tierKey);

                    // Add null check for tierDetails to satisfy TypeScript
                    if (!tierDetails) return null;

                    return (
                      <Box key={tierKey} sx={{ mb: 5 }}>
                        <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider', pb: 1, mb: 3 }}>
                          {tierDetails.icon} {tierDetails.label} {/* Already translated via getTierDetails */}
                        </Typography>
                        
                        {modulesInTier && Array.isArray(modulesInTier) ? 
                          modulesInTier.map((module: Module, moduleIndex: number) => {
                          if (!module) return null;
                          const moduleTierDetails = getTierDetails(module.accessTier);
                          const headerBgColor = 
                            module.accessTier === 'Premium' ? 'rgba(255, 193, 7, 0.08)' : 
                            module.accessTier === 'Complete' ? 'rgba(46, 125, 50, 0.08)' : 
                            'action.hover'; 
                            
                          return (
                            <Accordion 
                              key={module.id} 
                              sx={{ mb: 2, boxShadow: 1, '&:before': { display: 'none' } }}
                              expanded={expandedModule === module.id}
                              onChange={handleAccordionChange(module.id)}
                            >
                              <AccordionSummary>
                                <Typography variant="h6">{module.title && module.title[language] ? module.title[language] : ''} (#{moduleIndex + 1})</Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Typography variant="body1" sx={{ mb: 3 }}>{module.description && module.description[language] ? module.description[language] : ''}</Typography>
                                
                                <Grid container spacing={3}>
                                   {/* Left Column: Module Items (PDFs) */}
                                   <Grid item xs={12} md={7}>
                                      <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                          <ArticleIcon fontSize="small" sx={{ mr: 0.5 }}/> {translations[language].myContent.moduleMaterialsTitle} {/* Translate */}
                                       </Typography>
                                      <Box>
                                       {module.items && Array.isArray(module.items) ? 
                                         [...module.items].sort((a, b) => a.order - b.order).map((item) => {
                                           if (!item) return null;
                                           return (
                                             <Card key={item.id} variant="outlined" sx={{ display: 'flex', mb: 1.5, alignItems: 'center' }}>
                                                <CardContent sx={{ flexGrow: 1, py: 1, px: 2 }}>
                                                 <Typography variant="body1" component="div" sx={{ fontWeight: 500 }}>
                                                   {item.order || 0}. {item.title && item.title[language] ? item.title[language] : ''}
                                                 </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1}}>
                                                  {item.description && item.description[language] ? item.description[language] : ''}
                                                </Typography>
                                              </CardContent>
                                              <Box sx={{ px: 2, py: 1 }}>
                                                <Button href={item.pdfUrl} target="_blank" rel="noopener noreferrer" download={item.fileName} variant="text" size="small" startIcon={<DownloadIcon />} >
                                                  {translations[language].myContent.downloadButton} {/* Translate */}
                                                </Button>
                                               </Box>
                                           </Card>
                                           );
                                         }) : null
                                       }
                                      </Box>
                                   </Grid>

                                   {/* Right Column: Module Intro Video */}
                                   <Grid item xs={12} md={5}>
                                     <Box sx={{ position: { md: 'sticky' }, top: { md: '100px' } }}>
                                        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                            <OndemandVideoIcon fontSize="small" sx={{ mr: 0.5 }}/> {translations[language].myContent.moduleVideoTitle} {/* Translate */}
                                         </Typography>
                                         <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: 1, boxShadow: 1 }}>
                                           <iframe 
                                              src={module.introVideoUrl} 
                                              title={`Introductie Video: ${module.title[language]}`}
                                              frameBorder="0" 
                                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                              allowFullScreen
                                              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                                           >
                                           </iframe>
                                        </Box>
                                     </Box>
                                   </Grid>
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          );
                        }) : null}
                      </Box>
                    );
                   })
                  )}
              </Box> 
          </Box>
        )}
      </Container>

      {/* Example of Essential tier content */}
      <ProtectedContent requiredTier={SUBSCRIPTION_TIERS.ESSENTIAL}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            {language === 'nl' ? 'Essential Cursus: Carrière Oriëntatie' : 'Essential Course: Career Orientation'}
          </Typography>
          <Typography variant="body1" paragraph>
            {language === 'nl' 
              ? 'Deze module helpt je om jouw waarden, interesses en sterke punten te identificeren om een beter inzicht te krijgen in welke carrièrerichtingen bij je passen.'
              : 'This module helps you identify your values, interests, and strengths to get a better understanding of which career paths match your profile.'}
          </Typography>
          {/* Essential content here */}
        </Box>
      </ProtectedContent>

      {/* Example of Gold tier content */}
      <ProtectedContent 
        requiredTier={SUBSCRIPTION_TIERS.GOLD}
        title={language === 'nl' ? 'Gold Cursus: CV Masterclass' : 'Gold Course: CV Masterclass'}
        description={language === 'nl' 
          ? 'Deze premium cursus is beschikbaar voor Gold abonnees of hoger.' 
          : 'This premium course is available to Gold subscribers or higher.'}
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            {language === 'nl' ? 'CV Masterclass: Maak Indruk' : 'CV Masterclass: Make an Impression'}
          </Typography>
          <Typography variant="body1" paragraph>
            {language === 'nl' 
              ? 'Leer hoe je een professioneel CV maakt dat opvalt bij recruiters en jouw kansen op een uitnodiging voor een sollicitatiegesprek vergroot.'
              : 'Learn how to create a professional CV that stands out to recruiters and increases your chances of getting an interview invitation.'}
          </Typography>
          {/* Gold content here */}
        </Box>
      </ProtectedContent>

      {/* Example of Elite tier content */}
      <ProtectedContent requiredTier={SUBSCRIPTION_TIERS.ELITE}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            {language === 'nl' ? 'Elite Training: Persoonlijke Branding' : 'Elite Training: Personal Branding'}
          </Typography>
          <Typography variant="body1" paragraph>
            {language === 'nl' 
              ? 'Ontdek hoe je een sterke persoonlijke merkidentiteit kunt opbouwen die je onderscheidt in je vakgebied.'
              : 'Discover how to build a strong personal brand identity that distinguishes you in your field.'}
          </Typography>
          {/* Elite content here */}
        </Box>
      </ProtectedContent>

      {/* Example of Platinum tier content */}
      <ProtectedContent requiredTier={SUBSCRIPTION_TIERS.PLATINUM}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            {language === 'nl' ? 'Platinum Programma: Executive Coaching' : 'Platinum Program: Executive Coaching'}
          </Typography>
          <Typography variant="body1" paragraph>
            {language === 'nl' 
              ? 'Exclusieve coaching-sessies en geavanceerde materialen om je carrière naar het hoogste niveau te tillen.'
              : 'Exclusive coaching sessions and advanced materials to elevate your career to the highest level.'}
          </Typography>
          {/* Platinum content here */}
        </Box>
      </ProtectedContent>
    </>
  );
};

export default MyContent; 
